import {
	EventFilterCondition,
	EventFilterValue,
	EventFilterView,
	EventViewPrefilter,
	FilterCategory,
	GamePageResponse,
	GamePageLazyResponse,
	GPEventResponse,
} from "../../../types";
import filterViews, { EventFilterViewKey } from "./definition";

export default class EventFilter {
	filterViews: EventFilterView[] = [];
	flatFilterViews: EventFilterView[] = [];
	events: GPEventResponse[] = [];
	allEvents: GPEventResponse[] = [];
	private homePlayerIds: Set<string> = new Set();
	private awayPlayerIds: Set<string> = new Set();
	private playerOptions: Map<string, { filterKey: "player.home" | "player.away"; label: string }> = new Map();
	private allowedPlayerIds: Set<string> = new Set();
	private temporarilyAllowedPlayerId: string | null = null;

	constructor(gameData: GamePageResponse | undefined = undefined, gameDataLazy: GamePageLazyResponse | undefined = undefined) {
		if (gameData !== undefined && gameDataLazy !== undefined) {
			this.filterViews = filterViews;
			this.events = gameDataLazy.events.filter((e) => !["start_of_half", "end_of_half"].includes(e.type));
			this.allEvents = gameDataLazy.events;
			this.hydratedFilters(gameData, gameDataLazy);
			this.flatFilterView(this.filterViews);
		} else {
			this.filterViews = [];
			this.events = [];
			this.allEvents = [];
		}
	}

	/**
	 * hydrates data based on game content, like teams, players and highlights
	 */
	private hydratedFilters(gameData: GamePageResponse, eventsSource: GamePageLazyResponse): void {
		const homeTeamFilter = this.filterViews.find((f: EventFilterView) => f.key === "team.home");
		const awayTeamFilter = this.filterViews.find((f: EventFilterView) => f.key === "team.away");
		const allowedPlayers: number[] | null = gameData.permissions.allowedPlayers;

		if (homeTeamFilter) {
			homeTeamFilter.label = gameData.homeGameTeam.clubName || gameData?.homeGameTeam.teamName || homeTeamFilter.label;
			homeTeamFilter.value = gameData.homeGameTeam.id.toString();

			const homePlayersFilter = homeTeamFilter.nested?.find((f: EventFilterView) => f.key === "player.home");
			if (homePlayersFilter) {
				const homePlayerMap = new Map<string, string>();
				gameData.homeGameTeam.players.forEach((player) => {
					if (player.id !== null && player.name !== null) {
						const id = player.id.toString();
						const label = `${player.numberSign} ${player.name}`;

						this.homePlayerIds.add(id);
						this.playerOptions.set(id, { filterKey: "player.home", label });

						if (allowedPlayers === null || allowedPlayers.includes(player.id)) {
							homePlayerMap.set(id, label);
							this.allowedPlayerIds.add(id);
						}
					}
				});
				homePlayersFilter.options = homePlayerMap;
			}
		}

		if (awayTeamFilter) {
			awayTeamFilter.label = gameData.awayGameTeam.clubName || gameData?.awayGameTeam.teamName || awayTeamFilter.label;
			awayTeamFilter.value = gameData.awayGameTeam.id.toString();

			const awayPlayersFilter = awayTeamFilter.nested?.find((f: EventFilterView) => f.key === "player.away");
			if (awayPlayersFilter) {
				const awayPlayerMap = new Map<string, string>();
				gameData.awayGameTeam.players.forEach((player) => {
					if (player.id !== null && player.name !== null) {
						const id = player.id.toString();
						const label = `${player.numberSign} ${player.name}`;

						this.awayPlayerIds.add(id);
						this.playerOptions.set(id, { filterKey: "player.away", label });

						if (allowedPlayers === null || allowedPlayers.includes(player.id)) {
							awayPlayerMap.set(id, label);
							this.allowedPlayerIds.add(id);
						}
					}
				});
				awayPlayersFilter.options = awayPlayerMap;
			}
		}

		const filtersToRemove: string[] = [];
		[1, 2, 3, 4, 5].forEach((period: number) => {
			const periodEvent = eventsSource.events.find((e) => e.half === period);

			if (periodEvent === undefined) {
				filtersToRemove.push(`period.${period}`);
			}
		});

		const highlightFilter = this.filterViews
			.find((f: EventFilterView) => f.key === "highlight")
			?.nested
			?.find((f: EventFilterView) => f.key === "highlight.item");

		const highlights = eventsSource.events.filter((event: GPEventResponse) => event.type === "highlight");
		const highlightOptionsMap = new Map<string, string>();
		if (highlightFilter) {
			highlights.forEach((event: GPEventResponse) => {
				if (event.details && event.details.title) {
					highlightOptionsMap.set(
						event.id.toString(),
						event.details.title as string,
					)
				}
			});
			highlightFilter.options = highlightOptionsMap;
		}

		if (highlights.length === 0) {
			filtersToRemove.push("highlight.item");
		}

		this.filterViews = this.filterViews.filter((fw) => !filtersToRemove.includes(fw.key));
	}

	private flatFilterView(filterViews: EventFilterView[]): void {
		filterViews.forEach((fv) => {
			this.flatFilterViews.push({ ...fv });

			if (Array.isArray(fv.nested)) {
				this.flatFilterView(fv.nested);
			}
		});
	}

	filter(filters: EventFilterValue[]): GPEventResponse[] {
		const baseEvents = new Map<number, GPEventResponse>();
		const periodEvents = new Map<number, GPEventResponse>();
		const highlightEvents = new Map<number, GPEventResponse>();
		let hasTeamOrPlayerFilter = false;
		let hasPeriodFilter = false;
		let hasHasCommentsFilter = false;

		// first step merge all players' and teams' events
		filters.forEach((f) => {
			const filterCategory = this.getFilterCategory(f.key);
			if ([FilterCategory.Team, FilterCategory.Player].includes(this.getFilterCategory(f.key))) {
				hasTeamOrPlayerFilter = true;
			}

			if (filterCategory === FilterCategory.Team && this.getNestedFilterValues(f.key, filters).length === 0) {
				hasTeamOrPlayerFilter = true;
				this.applyFilter(this.events, f.key, f.value || f.key, filters).forEach((e) => {
					baseEvents.set(e.id, e);
				});
			}

			if (filterCategory === FilterCategory.Player) {
				hasTeamOrPlayerFilter = true;
				this.applyFilter(this.events, f.key, f.value || f.key, filters).forEach((e) => {
					baseEvents.set(e.id, e);
				});
			}

			if (this.getFilterCategory(f.key) === FilterCategory.Period) {
				hasPeriodFilter = true;
				this.applyFilter(this.events, f.key, f.value || f.key, filters).forEach((e) => {
					periodEvents.set(e.id, e);
				});
			}

			if (filterCategory === FilterCategory.HasComments) {
				hasHasCommentsFilter = true;
				this.applyFilter(this.events, f.key, f.value || f.key, filters).forEach((e) => {
					baseEvents.set(e.id, e);
				});
			}
		});

		// add all events if thea are not prefiltered by team or player
		if (!hasTeamOrPlayerFilter && !hasPeriodFilter && !hasHasCommentsFilter) {
			this.events.forEach((e) => {
				if (e.gameTeamId !== null) {
					baseEvents.set(e.id, e);
				}
			});
		}

		// intersect team player and period events
		if (hasTeamOrPlayerFilter && hasPeriodFilter) {
			baseEvents.forEach((e) => {
				if (!periodEvents.has(e.id)) {
					baseEvents.delete(e.id);
				}
			});
		}

		// add period events to base if no team or player selected
		if (!hasTeamOrPlayerFilter && hasPeriodFilter) {
			periodEvents.forEach((e) => {
				baseEvents.set(e.id, e);
			});
		}

		const eventBuckets: Set<GPEventResponse>[] = [];

		// second step limit by event categories if there are no nested event filters
		filters.forEach((f) => {
			if (this.getFilterCategory(f.key) === FilterCategory.EventGroup) {
				if (this.getNestedFilterValues(f.key, filters).length === 0) {
					eventBuckets.push(new Set(this.applyFilter(Array.from(baseEvents.values()), f.key, "", filters)));
				}
			}
		});

		// third step limit by event type and nested flag filters
		filters.forEach((f) => {
			if (this.getFilterCategory(f.key) === FilterCategory.Event) {
				let eventEvents = this.applyFilter(Array.from(baseEvents.values()), f.key, f.value, filters);

				this.getNestedFilterValues(f.key, filters).forEach((ff) => {
					eventEvents = this.applyFilter(eventEvents, ff.key, ff.value, filters);
				});

				eventBuckets.push(new Set(eventEvents));
			}
		});

		const filteredEvents = new Map<number, GPEventResponse>;

		if (eventBuckets.length !== 0) {
			eventBuckets.forEach((b) => {
				b.forEach((e) => {
					filteredEvents.set(e.id, e)
				});
			});
		} else {
			baseEvents.forEach((e) => {
				filteredEvents.set(e.id, e);
			});
		}

		// fourth step adds highlight events if no filters are applied or highlight filters are set
		const highlightGroupFilter = filters.find((fv) => this.getFilterCategory(fv.key) === FilterCategory.HighlightGroup);
		const highlightFilter = filters.find((fv) => this.getFilterCategory(fv.key) === FilterCategory.HighlightItem);
		const nonHighlightFiltersCount = filters.length - (highlightFilter ? 1 : 0) - (highlightGroupFilter ? 1 : 0);

		if (highlightFilter) {
			this.applyFilter(this.events, highlightFilter.key, highlightFilter.value, filters).forEach((e) => {
				highlightEvents.set(e.id, e);
			});
		} else {
			this.applyFilter(this.events, "highlight", ["highlight"], filters).forEach((e) => {
				highlightEvents.set(e.id, e);
			});
		}

		if (nonHighlightFiltersCount === 0 && (highlightGroupFilter || highlightFilter)) {
			filteredEvents.clear();
		}

		if (nonHighlightFiltersCount === 0 || highlightGroupFilter || highlightFilter) {
			highlightEvents.forEach((e) => {
				filteredEvents.set(e.id, e);
			});
		}

		return Array.from(filteredEvents.values());
	}

	filterByConditions(conditions: EventFilterCondition[], events: GPEventResponse[]): GPEventResponse[] {
		conditions.forEach((condition: EventFilterCondition) => {
			switch (condition.type) {
				case "id":
					events = events?.filter((e) => condition.value.includes(e.id.toString()));
					break;
				case "type":
					events = events?.filter((e) => condition.value.includes(e.type));
					break;
				case "period":
					events = events?.filter((e) => condition.value.includes(e.half?.toString() || "--"));
					break;
				case "success":
					if (condition.value.includes("true")) {
						events = events?.filter((e) => e.success === true);
					} else if (condition.value.includes("false")) {
						events = events?.filter((e) => e.success === false);
					}
					break;
				case "under_pressure":
					if (condition.value.includes("true")) {
						events = events?.filter((e) => e.underPressure === true);
					} else if (condition.value.includes("false")) {
						events = events?.filter((e) => e.underPressure === false);
					}
					break;
				case "player":
					events = events?.filter((e) => condition.value.includes(e.gamePlayerId?.toString() || ""));
					break;
				case "team":
					events = events?.filter((e) => condition.value.includes(e.gameTeamId?.toString() || ""));
					break;
				case "flag":
					events = events?.filter((e) => {
						const detail = e.details?.[condition.flag as string] as string | boolean | undefined;

						if (e.details === null || detail === undefined) {
							return false;
						}

						if (detail === true && condition.value.includes("true")) {
							return true;
						}

						if (detail === false && condition.value.includes("false")) {
							return true;
						}

						return condition.value.includes(detail as string);
					});
					break;
				case "hasComments":
					if (condition.value.includes("true")) {
						events = events.filter(e => Array.isArray(e.comments) && e.comments.length > 0);
					} else if (condition.value.includes("false")) {
						events = events.filter(e => !Array.isArray(e.comments) || e.comments.length === 0);
					}
					break;
			}
		});

		return events || [];
	}

	filterByCombinedConditions(conditions: EventFilterCondition[], events: GPEventResponse[]): GPEventResponse[] {
		if (!conditions || conditions.length === 0) return events || [];

		return events?.filter((e) => {
			// Only apply filters if those condition types are present
			const passesPlayer = !conditions.some(c => c.type === "player") ||
				conditions.some(c => c.type === "player" && c.value.includes(e.gamePlayerId?.toString() || ""));

			const passesTeam = !conditions.some(c => c.type === "team") ||
				conditions.some(c => c.type === "team" && c.value.includes(e.gameTeamId?.toString() || ""));

			const passesPeriod = !conditions.some(c => c.type === "period") ||
				conditions.some(c => c.type === "period" && c.value.includes(e.half?.toString() || "--"));

			return passesPlayer && passesTeam && passesPeriod;
		}) || [];
	}

	getParentFilterView(key: string): EventFilterView | undefined {
		const keyParts: string[] = key.split(".");

		if (this.getFilterCategory(key) === FilterCategory.Player) {
			return this.flatFilterViews.find((f) => f.key === `team.${keyParts[1]}`);
		}

		if (this.getFilterCategory(key) === FilterCategory.Event) {
			return this.flatFilterViews.find((f) => f.key === `category.${keyParts[1]}`);
		}

		if (this.getFilterCategory(key) === FilterCategory.EventFlag) {
			return this.flatFilterViews.find((f) => f.key === `event.${keyParts[1]}.${keyParts[2]}` && f.key.split(".").length === 3);
		}

		if (this.getFilterCategory(key) === FilterCategory.HighlightItem) {
			return this.flatFilterViews.find((f) => f.key === "highlight");
		}

		return undefined;
	}

	getNestedFilterViews(key: string): EventFilterView[] {
		const keyParts: string[] = key.split(".");

		if (this.getFilterCategory(key) === FilterCategory.Team) {
			return this.flatFilterViews.filter((f) => f.key === `player.${keyParts[1]}`);
		}

		if (this.getFilterCategory(key) === FilterCategory.EventGroup) {
			return this.flatFilterViews.filter((f) => f.key.startsWith(`event.${keyParts[1]}`) && f.key.split(".").length === 3);
		}

		if (this.getFilterCategory(key) === FilterCategory.Event) {
			return this.flatFilterViews.filter((f) => f.key.startsWith(`event.${keyParts[1]}.${keyParts[2]}`) && f.key.split(".").length === 4);
		}

		if (this.getFilterCategory(key) === FilterCategory.HighlightGroup) {
			return this.flatFilterViews.filter((f) => f.key === "highlight.item");
		}

		return [];
	}

	eventsCount(key: string, value: string | string[], filterValues: EventFilterValue[]): number {
		const isEventOrCategoryFilter = !this.isHighlightFilter(key) && [FilterCategory.Event, FilterCategory.EventGroup].includes(this.getFilterCategory(key));
		const isPeriodFilter = this.getFilterCategory(key) === FilterCategory.Period;
		const isFlagFilter = this.getFilterCategory(key) === FilterCategory.EventFlag;
		const teamOrPlayerFilters = filterValues.filter((fv) => [FilterCategory.Team, FilterCategory.Player].includes(this.getFilterCategory(fv.key)));
		const teamPlayerOrPeriodFilters = filterValues.filter((fv) => [FilterCategory.Team, FilterCategory.Player, FilterCategory.Period].includes(this.getFilterCategory(fv.key)));
		let eventsSource = this.events;

		if (isFlagFilter) {
			const parentFilterView = this.getParentFilterView(key);

			eventsSource = [];
			if (parentFilterView) {
				const flagFilters = [...teamPlayerOrPeriodFilters];
				flagFilters.push({ key: parentFilterView.key, value: [parentFilterView.value] } as EventFilterValue)
				eventsSource = this.filter(flagFilters);
			}
		}

		if (isPeriodFilter && teamOrPlayerFilters.length !== 0) {
			eventsSource = this.filter(teamOrPlayerFilters);
		}

		if (isEventOrCategoryFilter && teamPlayerOrPeriodFilters.length !== 0) {
			eventsSource = this.filter(teamPlayerOrPeriodFilters);
		}

		return this.applyFilter(eventsSource, key, value, filterValues).length;
	}

	getFilterCategory(key: string): FilterCategory {
		const keyParts: string[] = key.split(".");

		switch (true) {
			case keyParts[0] === "hasComments": return FilterCategory.HasComments;
			case keyParts[0] === "team": return FilterCategory.Team;
			case keyParts[0] === "player": return FilterCategory.Player;
			case keyParts[0] === "period": return FilterCategory.Period;
			case keyParts[0] === "category": return FilterCategory.EventGroup;
			case keyParts[0] === "event" && keyParts.length === 3:
			case keyParts[0] === "event" && keyParts.length === 2:
				return FilterCategory.Event;
			case keyParts[0] === "event" && keyParts.length === 4: return FilterCategory.EventFlag;
			case key === "highlight": return FilterCategory.HighlightGroup;
			case key === "highlight.item": return FilterCategory.HighlightItem;
			case keyParts[0] === "divider": return FilterCategory.Divider;
		}

		throw new Error(`unsupported filter key ${key}`);
	}

	applyFilter(events: GPEventResponse[], key: string, value: string | string[], filterValues: EventFilterValue[]): GPEventResponse[] {

		if (this.flatFilterViews.find((fw) => fw.key === key) === undefined) {
			return events;
		}

		let filteredEvents = events;
		const singleValue = Array.isArray(value) ? Array.from([...value]).pop() as string : value;
		const arrayValue = Array.from([...value]);

		switch (this.getFilterCategory(key)) {
			case FilterCategory.Team:
				return this.filterByConditions([{
					type: "team",
					value: arrayValue,
				}], events);
			case FilterCategory.Player:
				return this.filterByConditions([{
					type: "player",
					value: arrayValue,
				}], events);
			case FilterCategory.Period:
				return this.filterByConditions([{
					type: "period",
					value: arrayValue,
				}], events);
			case FilterCategory.Event:
				if (key == EventFilterViewKey.EventId) {
					const event = events.find((e) => e?.id?.toString() == value?.[0]);
					return event ? [event] : [];
				}

				const filterView = this.flatFilterViews.find((fv) => fv.key === key) as EventFilterView;

				if (filterView.prefilter) {
					return this.prefilterEvents(events, filterView.prefilter as EventViewPrefilter);
				}

				return [];
			case FilterCategory.EventGroup:
				const nestedFilteredEvents = new Map<number, GPEventResponse>();

				this.getNestedFilterViews(key).forEach((fv) => {
					this.applyFilter(events, fv.key, [fv.value as string], filterValues).forEach((e) => {
						nestedFilteredEvents.set(e.id, e);
					});
				})

				return Array.from(nestedFilteredEvents.values());
			case FilterCategory.EventFlag:
				// apply on prefiltered events by type only!
				const flagType = key.split(".").pop() as string;

				if (Array.isArray(value) && value.length !== 0) {
					switch (flagType) {
						case "goal_contribution":
							switch (singleValue) {
								case "na":
									filteredEvents = filteredEvents.filter((e) =>
										e.details === null
										|| (
											!e.details.hasOwnProperty("assist")
											&& !e.details.hasOwnProperty("goal"))
									);
									break;
								case "goal":
									filteredEvents = filteredEvents.filter((e) =>
										e.details !== null
										&& e.details.hasOwnProperty("goal")
										&& e.details["goal"] === true
									);
									break;
								case "assist":
									filteredEvents = filteredEvents.filter((e) =>
										e.details !== null
										&& e.details.hasOwnProperty("assist")
										&& e.details["assist"] === true
									);
									break;
							}
							break;
						case "event_type":
							return this.filterByConditions([{
								type: "type",
								value: value,
							}], events);
						case "situation":
							switch (singleValue) {
								case "play":
									filteredEvents = filteredEvents.filter((e) =>
										e.details !== null
										&& e.details.hasOwnProperty("goal_type")
										&& e.details["goal_type"] === "play"
									);
									break;
								case "penalty":
									filteredEvents = filteredEvents.filter((e) =>
										e.details !== null
										&& e.details.hasOwnProperty("goal_type")
										&& ["penalty", "shootout"].includes(String(e.details["goal_type"]))
									);
									break;
							}
							break;
						case "gk_pass":
							switch (singleValue) {
								case "na":
									filteredEvents = filteredEvents.filter((e) =>
										e.details === null
										|| !e.details.hasOwnProperty("gk_pass")
									);
									break;
								default:
									filteredEvents = filteredEvents.filter((e) =>
										e.details !== null
										&& e.details.hasOwnProperty("gk_pass")
										&& arrayValue.includes(String(e.details["gk_pass"]))
									);
									break;
							}
							break;
						default:
							const flag = key.split(".").pop() as string;

							if (["success", "under_pressure"].includes(flag)) {
								return this.filterByConditions([{
									type: flag,
									value: value,
								}], events);
							}

							return this.filterByConditions([{
								type: "flag",
								value: value,
								flag: key.split(".").pop(),
							}], events);
					}

					return filteredEvents;
				}

				break;
			case FilterCategory.HighlightGroup:
				return this.filterByConditions([{
					type: "type",
					value: arrayValue,
				}], events);
			case FilterCategory.HighlightItem:
				return this.filterByConditions([{
					type: "id",
					value: arrayValue,
				}], events);
			case FilterCategory.HasComments:
				return this.filterByConditions([{
					type: "hasComments",
					value: arrayValue,
				}], events);
			default:
		}

		return [];
	}

	isParentFilterActive(key: string, filters: EventFilterValue[]): boolean | undefined {
		const parentFilterView = this.getParentFilterView(key);

		if (parentFilterView === undefined) {
			return undefined;
		}

		return filters.find((f) => f.key === parentFilterView.key) !== undefined;
	}

	getNestedFilterValues(key: string, filterValues: EventFilterValue[]): EventFilterValue[] {
		const nestedFilterViewsKeys = this.getNestedFilterViews(key).map((fv) => fv.key);

		return filterValues.filter((f) => nestedFilterViewsKeys.includes(f.key));
	}

	hasNestedFilterValues(key: string, filterValues: EventFilterValue[]): boolean {
		return this.getNestedFilterValues(key, filterValues).length !== 0;
	}

	getFilterViewByFilterValue(filterValue: EventFilterValue): EventFilterView | undefined {
		return this.getFilterView(filterValue.key);
	}

	getFilterView(key: string): EventFilterView | undefined {
		return this.flatFilterViews.find((fv) => fv.key === key);
	}

	getFilterViewsByCategories(categories: FilterCategory[]): EventFilterView[] {
		return this.flatFilterViews.filter((fw) => categories.includes(this.getFilterCategory(fw.key)));
	}

	isHighlightFilter(key: string): boolean {
		return key.startsWith("highlight");
	}

	prefilterEvents(events: GPEventResponse[], prefilter: EventViewPrefilter | undefined): GPEventResponse[] {
		if (prefilter?.type) {
			events = this.filterByConditions([{
				type: "type",
				value: prefilter.type,
			}], events);
		}

		if (prefilter?.period) {
			events = this.filterByConditions([{
				type: "period",
				value: prefilter.period,
			}], events);
		}

		if (prefilter?.details) {
			Object.getOwnPropertyNames(prefilter?.details).forEach((property: string) => {
				events = this.filterByConditions([{
					type: "flag",
					flag: property,
					value: prefilter?.details?.[property] as string[],
				}], events);
			});
		}

		return events;
	}

	mutateFilterValues(filters: EventFilterValue[], key: string, value: string, isUnique: boolean = false, addOnly: boolean = false): EventFilterValue[] {
		if (this.flatFilterViews.find((fw) => fw.key === key) === undefined) {
			return filters;
		}

		let filterValue = filters.find((v) => v.key === key);

		if (!filterValue) {
			filterValue = {
				key: key,
				value: [] as string[],
			} as EventFilterValue;
			filters.push(filterValue);
		}

		const index = filterValue.value.indexOf(value as never);
		if (index > -1) {
			if (!addOnly) {
				filterValue.value.splice(index, 1);
			}
		} else {
			if (this.isParentFilterActive(key, filters) === false) {
				const parentFilter = this.getParentFilterView(key) as EventFilterView;
				this.mutateFilterValues(filters, parentFilter.key, parentFilter.value || parentFilter.key);
			}
			if (isUnique) {
				filterValue.value = [value];
			} else {
				filterValue.value.push(value as never);
			}
		}

		let newFilters: EventFilterValue[] = filters.filter((v) => v.value.length > 0);

		if (newFilters.find((f) => f.key === key) === undefined) {
			newFilters = this.removeNestedFilters(key, newFilters);
		}

		return newFilters;
	}

	// ? When unrestricted is true the permissions.allowedPlayers restriction is ignored
	mutateFilterValuesByPlayerId(filters: EventFilterValue[], playerId: number, unrestricted: boolean = false): EventFilterValue[] {
		if (unrestricted) {
			this.allowPlayerTemporarily(playerId.toString());
		}

		const isHomePlayer = unrestricted
			? this.homePlayerIds.has(playerId.toString())
			: this.getFilterView("player.home")?.options?.has(playerId.toString());
		if (isHomePlayer) {
			filters = this.mutateFilterValues(filters, "player.home", playerId.toString())
		}

		const isAwayPlayer = unrestricted
			? this.awayPlayerIds.has(playerId.toString())
			: this.getFilterView("player.away")?.options?.has(playerId.toString());
		if (isAwayPlayer) {
			filters = this.mutateFilterValues(filters, "player.away", playerId.toString())
		}

		return filters;
	}

	// Removes the player previously granted temporary access to the allowedPlayers restriction, if any
	clearTemporarilyAllowedPlayers(): void {
		if (this.temporarilyAllowedPlayerId === null) {
			return;
		}

		const filterKey = this.playerOptions.get(this.temporarilyAllowedPlayerId)?.filterKey;
		this.getFilterView(filterKey as string)?.options?.delete(this.temporarilyAllowedPlayerId);
		this.temporarilyAllowedPlayerId = null;
	}

	// Grants a player not present in permissions.allowedPlayers temporary access, replacing any previously granted player
	private allowPlayerTemporarily(playerId: string): void {
		this.clearTemporarilyAllowedPlayers();

		if (this.allowedPlayerIds.has(playerId)) {
			return;
		}

		const option = this.playerOptions.get(playerId);
		if (option === undefined) {
			return;
		}

		this.getFilterView(option.filterKey)?.options?.set(playerId, option.label);
		this.temporarilyAllowedPlayerId = playerId;
	}

	removeNestedFilters(key: string, filterValues: EventFilterValue[]) {
		this.getNestedFilterViews(key).forEach((fv) => {
			filterValues = this.removeNestedFilters(fv.key, filterValues);
			filterValues = filterValues.filter((f) => fv.key !== f.key);
		});

		return filterValues;
	}

	areFilterValuesSavable(filterValues: EventFilterValue[]): boolean {
		return filterValues.filter(
			(fv) => this.getFilterCategory(fv.key) !== FilterCategory.Player
		).length > 0;
	}
}
